// Iconiţa unei monede sau a unei burse.
//
// Regula e simplă: dacă avem logoul adevărat, îl arătăm; dacă nu, punem un cerculeţ cu
// primele două litere. Monograma nu e un logo inventat şi nu încearcă să pară unul — e
// felul cinstit de a spune „încă nu am poza asta". Un desen scornit de noi ar fi mai rău
// decât nimic: omul ar crede că aşa arată moneda.
//
// Ce nu facem: nu cerem imaginile de la CoinGecko din pagină. Patru sute de cereri către
// alt domeniu la fiecare deschidere ar fi şi încet, şi ar plimba adresa fiecărui
// vizitator pe la un terţ. Serverul le aduce o dată şi le ţine la el.

import { el, debounce } from '../core/util.js';

/**
 * Ce iconiţe are serverul, cu extensia fiecăreia.
 *
 * Se cere o singură dată. Fără lista asta, o pagină cu patru sute de monede din care
 * avem zece ar trimite trei sute nouăzeci de cereri care se întorc goale — exact
 * încărcarea de care voiam să scăpăm. Cu ea, cerem doar ce există.
 */
let avem = null;
let seIncarca = null;

function listaIconitelor() {
  if (avem) return Promise.resolve(avem);
  if (!seIncarca) {
    seIncarca = fetch('/api/icons/lista')
      .then((r) => r.json())
      .then((j) => { avem = j; return j; })
      .catch(() => { avem = { coin: {}, exchange: {} }; return avem; });
  }
  return seIncarca;
}

/** Ce am cerut deja în viaţa acestei file, ca să nu cerem de două ori. */
const cerute = new Set();
const deCerut = { coin: new Set(), exchange: new Set() };

/**
 * Cererile se strâng şi pleacă într-un singur pachet.
 *
 * O listă de patru sute de monede ar însemna patru sute de cereri dacă am întreba pentru
 * fiecare. Aşteptăm o clipă, adunăm tot ce lipseşte de pe ecran şi trimitem o dată.
 */
const trimite = debounce(() => {
  const corp = { coin: [...deCerut.coin], exchange: [...deCerut.exchange] };
  deCerut.coin.clear();
  deCerut.exchange.clear();
  if (!corp.coin.length && !corp.exchange.length) return;
  fetch('/api/icons/cere', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(corp),
  }).catch(() => { /* iconiţele sunt podoabă, nu conţinut */ });
}, 800);

function cere(fel, nume) {
  const cheie = `${fel}:${nume}`;
  if (cerute.has(cheie)) return;
  cerute.add(cheie);
  deCerut[fel].add(nume);
  trimite();
}

/** Moneda fără perechea de cotare: „SKRUSDT" -> „SKR". */
export function bazaSimbolului(simbol) {
  const s = String(simbol || '').toUpperCase();
  for (const q of ['USDT', 'USDC', 'USD', 'BTC', 'ETH', 'BNB', 'EUR', 'TRY']) {
    if (s.endsWith(q) && s.length > q.length) return s.slice(0, -q.length);
  }
  return s;
}

/**
 * Culoarea monogramei, scoasă din chiar numele monedei.
 *
 * Aceeaşi monedă primeşte mereu aceeaşi culoare, pe orice ecran şi după orice
 * reîncărcare. O culoare la întâmplare ar face lista să pară alta de fiecare dată.
 */
function culoarea(nume) {
  let h = 0;
  for (let i = 0; i < nume.length; i++) h = (h * 31 + nume.charCodeAt(i)) % 360;
  return `hsl(${h} 45% 32%)`;
}

function construieste(fel, nume, marime, rotund) {
  const cerc = el('span', {
    class: `ic-simbol${rotund ? '' : ' patrat'}`,
    style: { width: `${marime}px`, height: `${marime}px`, background: culoarea(nume),
      fontSize: `${Math.max(7, Math.round(marime * 0.42))}px` },
    title: nume,
  }, [el('span', { class: 'ic-mono', text: nume.slice(0, 3) })]);

  // Monograma e deja acolo. Poza se adaugă doar dacă serverul ne-a spus că o are, deci
  // nu pornim nicio cerere care s-ar întoarce goală.
  listaIconitelor().then((lista) => {
    const ext = lista[fel]?.[nume];
    if (!ext) { cere(fel, nume); return; }
    const poza = el('img', {
      class: 'ic-poza', alt: nume, width: marime, height: marime,
      src: `/icons/${fel === 'exchange' ? 'exchanges' : 'coins'}/${nume}.${ext}`,
      loading: 'lazy', decoding: 'async',
    });
    poza.onerror = () => poza.remove();
    poza.onload = () => cerc.classList.add('cu-poza');
    cerc.append(poza);
  });
  return cerc;
}

/** Iconiţa unei monede. Primeşte şi „SKRUSDT", şi „SKR". */
export function iconMoneda(simbol, marime = 20) {
  const nume = bazaSimbolului(simbol);
  return construieste('coin', nume, marime, true);
}

/** Iconiţa unei burse: „binance", „bybit", „okx"… */
export function iconBursa(cheie, marime = 18) {
  const nume = String(cheie || '').toUpperCase();
  return construieste('exchange', nume, marime, false);
}
